/**
 * Conversation Content Form Page (Backoffice)
 * Edita o crea un ítem de contenido (texto/audio/vídeo) de una práctica de conversación.
 */

import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { IconButton } from '@/components/atoms/IconButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { useConversationContent } from '@/hooks/useConversationData';
import { conversationBackofficeRoutes } from '@/constants/conversationBackofficeRoutes';
import type { StoryResource } from '@/types/backoffice.types';
import { ResourceForm, type ResourceFormData, type ResourceFormRef } from '@/components/molecules/ResourceForm';
import styles from './ConversationContentFormPage.module.scss';

export function ConversationContentFormPage() {
  const navigate = useNavigate();
  const { resourceId, contentId } = useParams<{ resourceId: string; contentId: string }>();
  const isEditing = Boolean(contentId);

  const resourceFormRef = useRef<ResourceFormRef>(null);
  const [sidebarContainer, setSidebarContainer] = useState<HTMLDivElement | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const { content, isLoading, saveContent } = useConversationContent(resourceId);

  const existingItem = isEditing ? content.find((c) => c.id === contentId) : undefined;

  const handleBack = () => {
    if (resourceId) {
      const url = conversationBackofficeRoutes.edit(resourceId);
      navigate(`${url}?tab=resources`);
    } else {
      navigate(-1);
    }
  };

  const handleSave = async (formData: ResourceFormData) => {
    await saveContent(formData);
    handleBack();
  };

  if (isLoading) {
    return (
      <div className={styles.contentForm}>
        <div className={styles.contentForm__loading}>Cargando...</div>
      </div>
    );
  }

  if (isEditing && !existingItem) {
    return (
      <div className={styles.contentForm}>
        <div className={styles.contentForm__loading}>Elemento no encontrado.</div>
      </div>
    );
  }

  if (isEditing && existingItem?.type === 'conversation') {
    return (
      <div className={styles.contentForm}>
        <div className={styles.contentForm__toolbar}>
          <div className={styles.contentForm__header}>
            <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver al contenido" />
            <h1 className={styles.contentForm__title}>Conversación generada por IA</h1>
          </div>
        </div>
        <p style={{ padding: '1rem' }}>
          Esta pieza se gestiona desde la pestaña «Contenido» con el panel de generación por IA.
        </p>
      </div>
    );
  }

  const mediaResource: StoryResource | undefined =
    existingItem && existingItem.type !== 'conversation' ? (existingItem as StoryResource) : undefined;

  return (
    <div className={styles.contentForm}>
      <div className={styles.contentForm__toolbar}>
        <div className={styles.contentForm__header}>
          <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver al contenido" />
          <h1 className={styles.contentForm__title}>
            {isEditing ? 'Editar elemento de contenido' : 'Añadir elemento de contenido'}
          </h1>
          <div className={styles.contentForm__headerActions}>
            <IconButton
              icon={Save}
              variant="primary"
              onClick={() => resourceFormRef.current?.submit()}
              aria-label="Guardar"
            />
            <IconButton
              icon={isSidebarOpen ? PanelRightClose : PanelRightOpen}
              onClick={() => setIsSidebarOpen((v) => !v)}
              aria-label={isSidebarOpen ? 'Ocultar panel de información' : 'Mostrar panel de información'}
            />
          </div>
        </div>
      </div>

      <div className={styles.contentForm__body} data-sidebar-open={isSidebarOpen}>
        <div className={styles.contentForm__main}>
          <ResourceForm
            ref={resourceFormRef}
            mode={isEditing ? 'edit' : 'create'}
            resource={mediaResource}
            storyType="general"
            nextOrder={content.length + 1}
            onSave={handleSave}
            onCancel={handleBack}
            layout="editor"
            sidebarPortalTarget={sidebarContainer}
          />
        </div>
        {isSidebarOpen && (
        <aside className={styles.contentForm__sidebar}>
          <Card className={styles.contentForm__sidebarCard}>
            <CardHeader>
              <CardTitle>Información</CardTitle>
            </CardHeader>
            <CardContent className={styles.contentForm__sidebarContent}>
              <div ref={(el) => setSidebarContainer(el)} />
            </CardContent>
          </Card>
        </aside>
        )}
      </div>
    </div>
  );
}

export default ConversationContentFormPage;
